/**
 * 彩票相关API封装
 * 使用 MongoDB 后端存储数据，localStorage 作为降级方案
 */

// API 基础地址
const API_BASE = '/api/v2/lottery';

/**
 * 获取当前登录用户ID
 */
function getCurrentUserId() {
  try {
    // 优先从 mongo_user 获取（MongoDB 登录用户）- 与 authStore.js 保持一致
    const mongoUserStr = localStorage.getItem('mongo_user');
    if (mongoUserStr) {
      const mongoUser = JSON.parse(mongoUserStr);
      return mongoUser.user_id || mongoUser._id;
    }
    // 降级：从普通 user 获取
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      return user.user_id || user.id || user._id;
    }
  } catch (e) {
    console.warn('获取用户ID失败:', e);
  }
  return null;
}

/**
 * 生成模拟期号（降级时使用）
 */
function getMockPeriod() {
  const now = new Date();
  const periodNum = String(now.getFullYear()).slice(2) +
    String(Math.floor(now.getMonth() * 4 + now.getDate() / 7) + 1).padStart(3, '0');

  const endTime = new Date();
  endTime.setHours(20, 0, 0, 0);
  if (endTime < now) {
    endTime.setDate(endTime.getDate() + 1);
  }

  return {
    success: true,
    data: {
      period: `2024${periodNum}`,
      endTime: endTime.toISOString(),
      drawTime: new Date(endTime.getTime() + 3600000).toISOString()
    }
  };
}

/**
 * 获取当前可售期号
 */
export async function getCurrentPeriod() {
  const userId = getCurrentUserId();
  
  // 未登录时使用模拟数据
  if (!userId) {
    return getMockPeriod();
  }

  try {
    const response = await fetch(`${API_BASE}/period/current`);
    const data = await response.json();
    if (data.success) {
      return data;
    }
    // API 失败降级到模拟数据
    console.warn('获取期号API失败，使用模拟数据');
    return getMockPeriod();
  } catch (err) {
    console.warn('获取期号失败，使用模拟数据:', err);
    return getMockPeriod();
  }
}

/**
 * 生成订单号（本地降级时使用）
 */
function generateOrderNo() {
  const now = new Date();
  const timestamp = now.getFullYear().toString() +
    (now.getMonth() + 1).toString().padStart(2, '0') +
    now.getDate().toString().padStart(2, '0');
  const random = Math.random().toString(36).substring(2, 10).toUpperCase();
  return `LT${timestamp}${random}`;
}

/**
 * 保存订单到本地存储（自动去重）
 */
export function saveOrderToLocal(order) {
  try {
    const savedOrders = localStorage.getItem('lottery_orders');
    const orders = savedOrders ? JSON.parse(savedOrders) : [];
    
    // 去重：检查是否已存在相同订单号
    const existingIndex = orders.findIndex(o => o.orderNo === order.orderNo);
    if (existingIndex !== -1) {
      // 已存在则更新，不新增
      orders[existingIndex] = order;
    } else {
      // 不存在则添加到前面
      orders.unshift(order);
    }
    
    localStorage.setItem('lottery_orders', JSON.stringify(orders));
  } catch (e) {
    console.warn('保存订单到本地失败:', e);
  }
}

/**
 * 创建本地订单（降级时使用）
 * 注意：本地模式下积分扣减也仅保存在本地，不会同步到服务器
 */
function createLocalOrder(params) {
  const { redBalls, blueBall, period, betCount = 1, multiple = 1 } = params;
  const orderNo = generateOrderNo();
  const costPoints = betCount * multiple * 10; // 10积分/注

  // 本地模式也扣减积分（保存在本地）
  try {
    const key = 'local_points_balance';
    const currentBalance = parseInt(localStorage.getItem(key) || '1000');
    if (currentBalance >= costPoints) {
      localStorage.setItem(key, (currentBalance - costPoints).toString());
    }
  } catch (e) {
    console.warn('本地积分扣减失败:', e);
  }

  const order = {
    orderNo,
    period,
    redBalls,
    blueBall,
    betAmount: costPoints,
    betTime: new Date().toISOString(),
    status: 'pending',
    isLocal: true, // 标记为本地订单
  };

  // 保存到本地
  saveOrderToLocal(order);

  return {
    success: true,
    data: order,
    message: '本地保存成功（离线模式）',
  };
}

/**
 * 提交彩票订单
 */
export async function submitLotteryOrder(params) {
  const userId = getCurrentUserId();
  
  // 未登录时使用本地模式
  if (!userId) {
    return createLocalOrder(params);
  }

  try {
    const response = await fetch(`${API_BASE}/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...params,
        userId: userId
      })
    });

    const data = await response.json();

    if (data.success) {
      // 成功后也保存一份到本地作为备份
      saveOrderToLocal(data.data);
    }

    return data;
  } catch (err) {
    console.warn('提交订单API失败，降级到本地模式:', err);
    return createLocalOrder(params);
  }
}

/**
 * 获取本地订单列表（降级时使用）
 */
function getLocalOrders(params = {}) {
  const { page = 1, pageSize = 20, status = 'all' } = params;

  const savedOrders = localStorage.getItem('lottery_orders');
  let orders = savedOrders ? JSON.parse(savedOrders) : [];

  // 模拟开奖（简单处理：超过2小时的订单视为已开奖）
  const now = new Date().getTime();
  orders = orders.map(order => {
    if (order.status === 'pending') {
      const betTime = new Date(order.betTime).getTime();
      if (now - betTime > 2 * 3600 * 1000) {
        // 模拟开奖结果
        const winningRed = Array.from({ length: 33 }, (_, i) => i + 1)
          .sort(() => Math.random() - 0.5)
          .slice(0, 6)
          .sort((a, b) => a - b)
          .map(n => String(n).padStart(2, '0'));
        const winningBlue = String(Math.floor(Math.random() * 16) + 1).padStart(2, '0');

        order.winningNumbers = {
          redBalls: winningRed,
          blueBall: winningBlue
        };

        // 判断是否中奖
        const matchRed = order.redBalls.filter(n => winningRed.includes(n)).length;
        const matchBlue = order.blueBall === winningBlue ? 1 : 0;

        if (matchRed >= 4 || (matchRed >= 1 && matchBlue === 1)) {
          order.status = 'won';
          order.prize = [0, 5, 10, 200, 3000][Math.min(matchRed, 4)] * 100;
        } else if (matchBlue === 1) {
          order.status = 'won';
          order.prize = 500; // 5元
        } else {
          order.status = 'lost';
          order.prize = 0;
        }
      }
    }
    return order;
  });

  // 保存更新后的状态
  localStorage.setItem('lottery_orders', JSON.stringify(orders));

  // 按状态筛选
  if (status !== 'all') {
    orders = orders.filter(o => o.status === status);
  }

  // 按时间倒序
  orders.sort((a, b) => new Date(b.betTime) - new Date(a.betTime));

  // 分页
  const total = orders.length;
  const start = (page - 1) * pageSize;
  const list = orders.slice(start, start + pageSize);

  return {
    success: true,
    data: {
      list,
      total,
      page,
      pageSize,
      hasMore: start + pageSize < total
    }
  };
}

/**
 * 查询彩票订单列表
 */
export async function getLotteryList(params = {}) {
  const userId = getCurrentUserId();
  
  // 未登录时使用本地数据
  if (!userId) {
    return getLocalOrders(params);
  }

  try {
    const { page = 1, pageSize = 20, status = 'all' } = params;
    const query = new URLSearchParams({
      userId: userId,
      page: String(page),
      pageSize: String(pageSize),
      status: status
    });

    const response = await fetch(`${API_BASE}/orders?${query}`);
    const data = await response.json();

    if (data.success) {
      return data;
    }

    console.warn('获取订单列表API失败，使用本地数据');
    return getLocalOrders(params);
  } catch (err) {
    console.warn('获取订单列表失败，使用本地数据:', err);
    return getLocalOrders(params);
  }
}

/**
 * 迁移本地订单到服务器
 */
export async function migrateLocalOrders() {
  const userId = getCurrentUserId();
  if (!userId) {
    return null;
  }

  try {
    const savedOrders = localStorage.getItem('lottery_orders');
    if (!savedOrders) {
      return null;
    }

    const orders = JSON.parse(savedOrders);
    if (orders.length === 0) {
      return null;
    }

    const response = await fetch(`${API_BASE}/migrate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, orders })
    });

    const data = await response.json();

    if (data.success) {
      console.log(`✅ 彩票订单迁移完成: ${data.data.migratedCount} 条已迁移, ${data.data.skippedCount} 条跳过`);
    }

    return data;
  } catch (err) {
    console.warn('订单迁移失败:', err);
    return null;
  }
}

/**
 * 查询订单详情
 */
export async function getLotteryDetail(orderNo) {
  const userId = getCurrentUserId();
  
  // 先查本地
  const savedOrders = localStorage.getItem('lottery_orders');
  if (savedOrders) {
    const orders = JSON.parse(savedOrders);
    const order = orders.find(o => o.orderNo === orderNo);
    if (order) {
      return {
        success: true,
        data: order
      };
    }
  }

  // 如果已登录，可以尝试从API获取（此处简化，直接返回失败）
  return {
    success: false,
    message: '订单不存在'
  };
}

/**
 * 获取用户积分
 */
export async function getUserPoints() {
  const userId = getCurrentUserId();
  if (!userId) {
    return { success: false, message: '请先登录' };
  }

  try {
    const response = await fetch(`${API_BASE}/user/points?userId=${userId}`);
    return await response.json();
  } catch (err) {
    console.warn('获取积分失败:', err);
    return { success: false, message: '获取积分失败' };
  }
}

/**
 * 自动迁移触发器
 * 登录后调用此函数自动迁移本地数据
 */
export function autoMigrateLocalOrders() {
  // 延迟1秒执行，避免阻塞登录流程
  setTimeout(() => {
    const userId = getCurrentUserId();
    if (userId) {
      migrateLocalOrders();
    }
  }, 1000);
}
